import type {
  CalendarDayRow,
  CalendarEarning,
  CalendarWeekColumn,
} from '~/types/earningsCalendar.types';

import { weekTabLabel } from '../constants/weekTabs.constants';

export type EarningsSearchHit = {
  earning: CalendarEarning;
  weekIndex: number;
  weekLabel: string;
  dayIndex: number;
  day: CalendarDayRow;
};

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/** True if every query char appears in order within `haystack`. */
function isSubsequence(query: string, haystack: string): boolean {
  let qi = 0;
  for (let i = 0; i < haystack.length && qi < query.length; i += 1) {
    if (haystack[i] === query[qi]) qi += 1;
  }
  return qi === query.length;
}

/**
 * Score a candidate against a normalized query.
 * Higher is better; 0 means no match.
 */
export function scoreFuzzyMatch(query: string, symbol: string, name: string): number {
  const q = normalize(query);
  if (!q) return 0;

  const sym = normalize(symbol);
  const nm = normalize(name);

  if (sym === q) return 100;
  if (sym.startsWith(q)) return 90;
  if (nm.startsWith(q)) return 80;
  if (sym.includes(q)) return 70;
  if (nm.includes(q)) return 60;
  if (isSubsequence(q, sym)) return 40;
  if (isSubsequence(q, nm)) return 30;
  return 0;
}

export function flattenCalendarForSearch(columns: CalendarWeekColumn[]): EarningsSearchHit[] {
  const hits: EarningsSearchHit[] = [];

  for (let weekIndex = 0; weekIndex < columns.length; weekIndex += 1) {
    const column = columns[weekIndex];
    const weekLabel = weekTabLabel(weekIndex);

    for (let dayIndex = 0; dayIndex < column.days.length; dayIndex += 1) {
      const day = column.days[dayIndex];
      const cell = column.cells[dayIndex] ?? [];
      for (const earning of cell) {
        hits.push({ earning, weekIndex, weekLabel, dayIndex, day });
      }
    }
  }

  return hits;
}

export function searchEarningsHits(
  hits: EarningsSearchHit[],
  query: string,
): EarningsSearchHit[] {
  const q = normalize(query);
  if (!q) return [];

  return hits
    .map((hit) => ({
      hit,
      score: scoreFuzzyMatch(q, hit.earning.symbol, hit.earning.name),
    }))
    .filter((row) => row.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aCap = a.hit.earning.marketCap;
      const bCap = b.hit.earning.marketCap;
      const aMissing = aCap == null || !Number.isFinite(aCap);
      const bMissing = bCap == null || !Number.isFinite(bCap);
      if (aMissing && bMissing) {
        return a.hit.earning.symbol.localeCompare(b.hit.earning.symbol);
      }
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (bCap !== aCap) return bCap - aCap;
      return a.hit.earning.symbol.localeCompare(b.hit.earning.symbol);
    })
    .map((row) => row.hit);
}
